import {
  IconDefinition,
  faMagnifyingGlass,
  faMagnifyingGlassMinus,
  faMagnifyingGlassPlus,
  faRotateLeft,
  faThumbTack as falThumbTack,
} from '@fortawesome/pro-light-svg-icons';
import { Button } from '@prio365/prio365-react-library';
import {
  CSSProperties,
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { PrioTheme } from '../../../theme/types';
import { makePrioStyles } from '../../../theme/utils';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateMailComponentsZoomFactor,
  updateMailComponentsZoomPanelPinnedState,
} from '../actions/components';
import {
  getMailComponentsZoomFactor,
  getMailComponentsZoomPanelPinnedState,
} from '../../../apps/main/rootReducer';
import { faThumbtack as fasThumbTack } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const MIN_ZOOM_LEVEL = 0.6;
const MAX_ZOOM_LEVEL = 3.25;

const useStyles = makePrioStyles((theme: PrioTheme) => ({
  root: {
    position: 'relative',
    overflow: 'hidden',
  },
  zoomControls: {
    position: 'absolute',
    padding: 16,
    top: 0,
    right: -200,
    opacity: 0.75,
    zIndex: 999,
    transition: 'right 0.3s ease-in-out 1s, opacity 0.3s ease-in-out',
    '& > div': {
      padding: 8,
      display: 'flex',
      alignItems: 'center',
      backgroundColor: theme.old.palette.backgroundPalette.content,
      borderRadius: 4,
      boxShadow: theme.old.palette.boxShadow.regular,
      gap: 8,
    },
    '&:hover': {
      right: 0,
      opacity: 1,
      '& $collapseButton': {
        '& .thumbtack': {
          display: 'block',
          visibility: 'visible',
        },
        '& .magnifying-glass': {
          display: 'none',
          visibility: 'hidden',
        },
      },
    },
    '&.active': {
      right: 0,
      opacity: 1,
    },
  },
  scrollContainer: {
    width: '100%',
    height: '100%',
    minWidth: '100%',
    minHeight: '100%',
    padding: 16,
  },
  collapseButton: {
    height: '100%',
    color: theme.colors.application.typography.default,
    minWidth: 20,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    '& .thumbtack': {
      display: 'none',
      visibility: 'hidden',
      transition: 'visibility 0.3s ease-out 1s, transform 0.2s ease-out',
    },
    '& .magnifying-glass': {
      display: 'block',
      visibility: 'visible',
      transition: 'visibility 0.3s ease-out 1s',
    },

    '& svg': {
      transform: 'rotate(0deg)',
      transition: 'transform 0.2s ease-out',
    },
    '&:not(.pinned)': {
      '& .thumbtack': {
        transform: 'rotate(45deg)',
      },
    },
    '&.pinned': {
      '& .thumbtack': {
        display: 'block',
        visibility: 'visible',
      },
      '& .magnifying-glass': {
        display: 'none',
        visibility: 'hidden',
      },
    },
  },
}));

export interface MailScrollContainerRef {
  enableHorizontalScroll: () => void;
  disableHorizontalScroll: () => void;
}

interface MailScrollContainerProps {
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}

const MailScrollContainer = forwardRef<
  MailScrollContainerRef,
  MailScrollContainerProps
>((props, ref) => {
  //#region ------------------------------ Defaults
  const { className, style, children } = props;

  const classes = useStyles();

  const dispatch = useDispatch();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const reduxZoomLevel = useSelector(getMailComponentsZoomFactor);

  const isZoomPanelPinned = useSelector(getMailComponentsZoomPanelPinnedState);

  const [zoomLevel, setZoomLevel] = useState<number>(reduxZoomLevel);
  const containerRef = useRef<HTMLDivElement>(null);

  const zoomControlsRef = useRef<HTMLDivElement>(null);

  const [isHorizontalScrollEnabled, setIsHorizontalScrollEnabled] =
    useState<boolean>(zoomLevel < 1);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const getNextZoomIn = useCallback((currentZoom: number): number => {
    if (currentZoom < 1.1) {
      return Math.min(1.1, currentZoom + 0.1);
    } else {
      return currentZoom + 0.25;
    }
  }, []);

  const getNextZoomOut = useCallback((currentZoom: number): number => {
    if (currentZoom <= 1.1) {
      return Math.max(0.1, currentZoom - 0.1);
    } else {
      return Math.max(1.1, currentZoom - 0.25);
    }
  }, []);

  const handleZoomIn = useCallback(() => {
    setZoomLevel((prev) => {
      if (prev < MAX_ZOOM_LEVEL) {
        return getNextZoomIn(prev);
      }
      return prev;
    });
  }, [getNextZoomIn]);

  const handleZoomOut = useCallback(() => {
    setZoomLevel((prev) => {
      if (prev > MIN_ZOOM_LEVEL) {
        return getNextZoomOut(prev);
      }
      return prev;
    });
  }, [getNextZoomOut]);

  const handleReset = useCallback(() => {
    setZoomLevel(1.0);
  }, []);

  const handleControlsCollapse = useCallback(() => {
    dispatch(updateMailComponentsZoomPanelPinnedState(!isZoomPanelPinned));
  }, [dispatch, isZoomPanelPinned]);

  const formatZoomLevel = (zoom: number): string => {
    return `${Math.round(zoom * 100)}%`;
  };
  //#endregion

  //#region ------------------------------ Ref
  useImperativeHandle(ref, () => ({
    enableHorizontalScroll: () => {
      setIsHorizontalScrollEnabled(true);
    },
    disableHorizontalScroll: () => {
      setIsHorizontalScrollEnabled(false);
    },
  }));
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      // Prüfen ob Ctrl (oder Cmd auf Mac) gedrückt ist
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault(); // Browser-Zoom unterbinden

        if (e.deltaY < 0) {
          // Nach oben scrollen = Zoom In
          handleZoomIn();
        } else {
          // Nach unten scrollen = Zoom Out
          handleZoomOut();
        }
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
    }
    return () => {
      if (container) {
        container.removeEventListener('wheel', handleWheel);
      }
    };
  }, [handleZoomIn, handleZoomOut]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case '0':
            e.preventDefault();
            handleReset();
            break;
          case '+':
          case '=':
            e.preventDefault();
            handleZoomIn();
            break;
          case '-':
            e.preventDefault();
            handleZoomOut();
            break;
        }
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('keydown', handleKeyDown);
      container.setAttribute('tabindex', '0'); // Macht Container fokussierbar
    }

    return () => {
      if (container) {
        container.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [handleZoomIn, handleZoomOut, handleReset]);

  useEffect(() => {
    dispatch(updateMailComponentsZoomFactor(zoomLevel));
  }, [dispatch, zoomLevel]);
  //#endregion

  return (
    <div
      className={classNames(classes.root, className)}
      ref={containerRef}
      style={style}
    >
      <div
        className={classes.scrollContainer}
        style={{
          overflowY: 'auto',
          overflowX:
            !isHorizontalScrollEnabled && zoomLevel < 1 ? 'hidden' : 'auto',
        }}
      >
        <div
          className={classNames(classes.zoomControls, {
            active: isZoomPanelPinned,
          })}
          ref={zoomControlsRef}
        >
          <div>
            <div
              className={classNames(classes.collapseButton, {
                pinned: isZoomPanelPinned,
              })}
              onClick={handleControlsCollapse}
            >
              <FontAwesomeIcon
                className="magnifying-glass"
                icon={faMagnifyingGlass}
              />
              <FontAwesomeIcon
                className="thumbtack"
                icon={
                  (isZoomPanelPinned
                    ? fasThumbTack
                    : falThumbTack) as IconDefinition
                }
              />
            </div>
            <Button
              type="default"
              iconProp={faMagnifyingGlassMinus}
              onClick={handleZoomOut}
              disabled={zoomLevel <= MIN_ZOOM_LEVEL}
            />

            <span
              style={{
                minWidth: '60px',
                textAlign: 'center',
              }}
            >
              {formatZoomLevel(zoomLevel)}
            </span>
            <Button
              type="default"
              iconProp={faMagnifyingGlassPlus}
              onClick={handleZoomIn}
              disabled={zoomLevel >= MAX_ZOOM_LEVEL}
            />
            <Button
              type="default"
              iconProp={faRotateLeft}
              onClick={handleReset}
            />
          </div>
        </div>
        <div
          style={{
            transform: `scale(${zoomLevel})`,
            transformOrigin: 'top left',
            width: zoomLevel === 1 ? '100%' : `calc(100% / ${zoomLevel})`,
            height: zoomLevel === 1 ? '100%' : `calc(100% / ${zoomLevel})`,
            position: 'relative',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
});

export default MailScrollContainer;
